import { useLocationDropdownFeatureFlag } from "@/core/splitio";
import { FilterLocation } from "@/data/partner_offering";
import { useQueryString, buildPathWithParams } from "@/hooks";
import { GalleryFilterSearchParams } from "@/offering/template_offering_listing_meeting_room";
import { uniqBy } from "lodash";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

export function useLocationParam(
  locations: FilterLocation[],
  isMultiSelect = true
) {
  const locationDropdownFlag = useLocationDropdownFeatureFlag();
  const searchParams = useQueryString<GalleryFilterSearchParams>();
  const history = useHistory();

  const locationIDParam = searchParams.locationID;
  const cityParam = searchParams.city;

  const derivedValues: { cities?: string[]; locationIDs?: string[] } =
    useMemo(() => {
      if (locationIDParam) {
        const cityOfLocationId = locations.find(
          (l) => l.id === locationIDParam
        )?.city;
        const numberOfLocationsInCity = uniqBy(
          locations.filter((l) => l.city === cityOfLocationId),
          (l) => l.id
        ).length;
        const selectedCityOfLocationId =
          numberOfLocationsInCity > 1 ? undefined : cityOfLocationId;

        return {
          cities: selectedCityOfLocationId
            ? [selectedCityOfLocationId]
            : undefined,
          locationIDs: [locationIDParam],
        };
      } else if (cityParam) {
        const locationIDsOfCityParam = uniqBy(
          locations.filter((l) => l.city === cityParam),
          (l) => l.id
        ).map((l) => l.id);

        return {
          cities: [cityParam],
          locationIDs: locationIDsOfCityParam,
        };
      } else {
        return {
          cities: searchParams.cities?.split(","),
          locationIDs: searchParams.locationIDs?.split(","),
        };
      }
    }, [
      cityParam,
      searchParams.cities,
      locationIDParam,
      locations,
      searchParams.locationIDs,
    ]);

  useEffect(() => {
    if (
      locationDropdownFlag &&
      (locationIDParam || cityParam) &&
      isMultiSelect
    ) {
      const pathWithParams = buildPathWithParams(window.location.pathname, {
        cities: derivedValues.cities,
        locationIDs: derivedValues.locationIDs,
        city: undefined,
        locationID: undefined,
      });
      history.replace(pathWithParams);
    }
  }, [
    locationIDParam,
    cityParam,
    history,
    locationDropdownFlag,
    derivedValues,
    isMultiSelect,
  ]);

  return derivedValues;
}
